import React, { useState, useEffect } from 'react';
import { Alert, Button, Form, Modal, Space, message } from 'antd';
import { useStore } from '../../hooks/storeContext';
// AdminLayout now provided at router level
import UserManagementService from '../../services/UserManagementService';
import VolunteerRegistrationService from '../../services/VolunteerRegistrationService';
import UserManagementHeader from './components/UserManagementHeader';
import FilterDrawer from './components/FilterDrawer';
import SearchFilterBar from './components/SearchFilterBar';
import UserDirectoryTable from './components/UserDirectoryTable';
import RegistrationsTable from './components/RegistrationsTable';
import UserFormModal from './components/UserFormModal';
import CredentialsModal from './components/CredentialsModal';
import WaiverModal from './components/WaiverModal';
import RegistrationWaiverModal from './components/RegistrationWaiverModal';
import SelectVolunteerModal from './components/SelectVolunteerModal';
import HcwAccessModal from './components/HcwAccessModal';
import {
  PageContainer,
  GlobalSelectStyles
} from './styled';

const UserManagement = () => {
  const { authStore } = useStore();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [filterDrawerVisible, setFilterDrawerVisible] = useState(false);
  const [form] = Form.useForm();
  
  // Permissions state
  const [selectedPermissions, setSelectedPermissions] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('admins');
  const [userCounts, setUserCounts] = useState({
    users: 0,
    volunteers: 0,
    admins: 0,
    registrations: 0
  });
  
  // Registrations state
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);
  const [registrationsPagination, setRegistrationsPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0
  });
  const [registrationStatusFilter, setRegistrationStatusFilter] = useState('all');

  // Deployed Hearts (HCW app) access
  const [hcwAccessUsers, setHcwAccessUsers] = useState([]);
  const [hcwBusyUserId, setHcwBusyUserId] = useState(null);
  const [selectedUserKeys, setSelectedUserKeys] = useState([]);
  const [selectedUserRows, setSelectedUserRows] = useState([]);
  
  // Pagination state
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
    showSizeChanger: true,
    showQuickJumper: true,
    showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} users`,
  });
  
  // Credentials modal state
  const [credentialsModal, setCredentialsModal] = useState({
    visible: false,
    userEmail: '',
    tempPassword: '',
    userName: ''
  });
  
  // Waiver modal state
  const [waiverModal, setWaiverModal] = useState({
    visible: false,
    loading: false,
    registrations: [],
    volunteerName: '',
    volunteerEmail: ''
  });
  
  // Registration waiver modal state
  const [registrationWaiverModal, setRegistrationWaiverModal] = useState({
    visible: false,
    registration: null
  });
  
  // Select volunteer modal state
  const [selectVolunteerModal, setSelectVolunteerModal] = useState({
    visible: false,
    loading: false,
    approvedRegistrations: [],
    creatingUserId: null
  });
  
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);
  
  // ── Initial load ──
  useEffect(() => {
    const initializePage = async () => {
      const loadDataPromise = loadUsers();
      const userCountsPromise = calculateUserCounts();
      
      await loadDataPromise;
      setInitialLoadComplete(true);
      
      userCountsPromise.catch(error => {
        console.error('Non-critical error loading user counts:', error);
      });
    };
    initializePage();
  }, []);
  
  // ── Filter change effects ──
  useEffect(() => {
    if (initialLoadComplete) {
      const params = {};
      if (searchText) params.search = searchText;
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      
      loadUsers(params);
    }
  }, [searchText, roleFilter, statusFilter, activeTab, initialLoadComplete]);

  useEffect(() => {
    if (initialLoadComplete && userCounts.users === 0 && userCounts.volunteers === 0 && userCounts.admins === 0) {
      calculateUserCounts();
    }
  }, [activeTab, initialLoadComplete]);
  
  // ── Load registrations when tab switches ──
  useEffect(() => {
    if (activeTab === 'registrations' && initialLoadComplete) {
      fetchRegistrations(registrationsPagination.current, registrationsPagination.pageSize);
    }
  }, [activeTab, registrationStatusFilter, initialLoadComplete]);

  // Selections don't carry across tabs.
  useEffect(() => {
    setSelectedUserKeys([]);
    setSelectedUserRows([]);
  }, [activeTab]);

  // Reset to first page on filter changes
  useEffect(() => {
    if (initialLoadComplete) {
      setPagination(prev => ({ ...prev, current: 1 }));
      loadUsers({ page: 1, limit: pagination.pageSize });
    }
  }, [searchText, roleFilter, statusFilter, initialLoadComplete, pagination.pageSize]);

  // ── Data loaders ──
  const calculateUserCounts = async () => {
    try {
      const [usersResponse, volunteersResponse, adminsResponse, registrationsResponse] = await Promise.all([
        UserManagementService.getUsers({ role: 'user', limit: 1 }),
        UserManagementService.getUsers({ role: 'volunteer', limit: 1 }),
        UserManagementService.getUsers({ role: 'admin', limit: 1 }),
        VolunteerRegistrationService.getAllRegistrations({ limit: 1 })
      ]);
      
      setUserCounts({
        users: usersResponse.pagination?.total || (usersResponse.data || usersResponse.users || []).length,
        volunteers: volunteersResponse.pagination?.total || (volunteersResponse.data || volunteersResponse.users || []).length,
        admins: adminsResponse.pagination?.total || (adminsResponse.data || adminsResponse.users || []).length,
        registrations: registrationsResponse.pagination?.total || (registrationsResponse.data || registrationsResponse.registrations || []).length
      });
    } catch (error) {
      console.error('Error calculating user counts:', error);
      setUserCounts({ users: 0, volunteers: 0, admins: 0, registrations: 0 });
    }
  };

  const loadUsers = async (params = {}) => {
    setLoading(true);
    try {
      const queryParams = {
        page: pagination.current,
        limit: pagination.pageSize,
        search: searchText,
        role: roleFilter !== 'all' ? roleFilter : '',
        status: statusFilter !== 'all' ? statusFilter : '',
        ...params
      };

      if (roleFilter === 'all') {
        if (activeTab === 'users') queryParams.role = 'user';
        else if (activeTab === 'volunteers') queryParams.role = 'volunteer';
        else if (activeTab === 'admins') queryParams.role = 'admin';
      }

      const response = await UserManagementService.getUsers(queryParams);
      setUsers(response.data || response.users || []);
      
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          current: response.pagination.page,
          total: response.pagination.total,
          pageSize: response.pagination.limit
        }));
      }
    } catch (error) {
      console.error('Error loading users:', error);
      message.error('Failed to load users. Please try again.');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchRegistrations = async (page = 1, pageSize = 10) => {
    setRegistrationsLoading(true);
    try {
      const params = { page, limit: pageSize };
      if (registrationStatusFilter !== 'all') params.status = registrationStatusFilter;
      
      const response = await VolunteerRegistrationService.getAllRegistrations(params);
      
      if (response.success) {
        setRegistrations(response.data || []);
        if (response.pagination) {
          setRegistrationsPagination({
            current: response.pagination.page,
            pageSize: response.pagination.limit,
            total: response.pagination.total
          });
        }
      } else {
        console.error('Failed to fetch registrations:', response.message);
        message.error(response.message || 'Failed to load registrations');
        setRegistrations([]);
      }
    } catch (error) {
      console.error('Error fetching registrations:', error);
      message.error('Failed to load registrations');
      setRegistrations([]);
    } finally {
      setRegistrationsLoading(false);
    }
  };

  // ── Registration handlers ──
  const handleUpdateRegistrationStatus = async (registrationId, status, notes = '') => {
    try {
      await VolunteerRegistrationService.updateStatus(registrationId, status, notes);
      message.success(`Registration ${status === 'approved' ? 'approved' : 'rejected'} successfully`);
      fetchRegistrations(registrationsPagination.current, registrationsPagination.pageSize);
      calculateUserCounts();
    } catch (error) {
      console.error('Error updating registration status:', error);
      message.error('Failed to update registration status');
    }
  };

  const fetchApprovedRegistrations = async () => {
    setSelectVolunteerModal(prev => ({ ...prev, loading: true }));
    try {
      const response = await VolunteerRegistrationService.getAllRegistrations({ 
        status: 'approved', limit: 100 
      });
      
      if (response.success) {
        setSelectVolunteerModal(prev => ({
          ...prev, loading: false, approvedRegistrations: response.data || []
        }));
      } else {
        message.error('Failed to load approved registrations');
        setSelectVolunteerModal(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('Error fetching approved registrations:', error);
      message.error('Failed to load approved registrations');
      setSelectVolunteerModal(prev => ({ ...prev, loading: false }));
    }
  };

  const handleCreateUserFromRegistration = async (registration) => {
    setSelectVolunteerModal(prev => ({ ...prev, creatingUserId: registration._id }));
    try {
      const tempPassword = Math.random().toString(36).slice(-8) + Math.random().toString(36).slice(-4).toUpperCase();
      
      const userData = {
        fullName: registration.fullName,
        email: registration.email,
        phone: registration.phoneNumber,
        role: 'volunteer',
        status: 'active',
        password: tempPassword,
        permissions: []
      };
      
      const response = await UserManagementService.createUser(userData);
      
      if (response.success || response.data) {
        message.success(`Volunteer account created for ${registration.fullName}`);
        setCredentialsModal({
          visible: true,
          userEmail: registration.email,
          tempPassword: tempPassword,
          userName: registration.fullName
        });
        setSelectVolunteerModal({ visible: false, loading: false, approvedRegistrations: [], creatingUserId: null });
        loadUsers();
        calculateUserCounts();
      } else {
        throw new Error(response.message || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user from registration:', error);
      message.error(error.message || 'Failed to create volunteer account');
    } finally {
      setSelectVolunteerModal(prev => ({ ...prev, creatingUserId: null }));
    }
  };

  // ── User CRUD handlers ──
  const handleAddUser = () => {
    if (activeTab === 'volunteers') {
      setSelectVolunteerModal(prev => ({ ...prev, visible: true }));
      fetchApprovedRegistrations();
      return;
    }
    
    setEditingUser(null);
    form.resetFields();
    setSelectedPermissions([]);
    setSelectedRole('user');
    form.setFieldsValue({
      fullName: '', email: '', phone: '',
      role: 'user', permissions: [], status: 'active'
    });
    setIsModalVisible(true);
  };

  const handleEditUser = async (user) => {
    try {
      setLoading(true);
      const response = await UserManagementService.getUserById(user._id);
      const userData = response.data;
      
      setEditingUser(userData);
      setSelectedPermissions(userData.permissions || []);
      setSelectedRole(userData.role || '');
      form.setFieldsValue({
        ...userData,
        permissions: userData.permissions || []
      });
      setIsModalVisible(true);
    } catch (error) {
      console.error('Error loading user data:', error);
      message.error('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    try {
      await UserManagementService.deleteUser(userId);
      message.success('User deleted successfully');
      loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      message.error('Failed to delete user. Please try again.');
    }
  };

  const handleModalOk = () => {
    form.validateFields()
      .then(async values => {
        const userData = { ...values, permissions: selectedPermissions };
        
        try {
          if (editingUser) {
            await UserManagementService.updateUser(editingUser._id, userData);
            message.success('User updated successfully');
            setIsModalVisible(false);
            form.resetFields();
            setSelectedPermissions([]);
            setSelectedRole('');
            loadUsers();
          } else {
            const response = await UserManagementService.createUser(userData);
            
            if (response.tempPassword) {
              setCredentialsModal({
                visible: true,
                userEmail: response.data?.email || userData.email,
                tempPassword: response.tempPassword,
                userName: response.data?.fullName || userData.fullName
              });
            } else {
              message.success('User created successfully');
            }
            
            setIsModalVisible(false);
            form.resetFields();
            setSelectedPermissions([]);
            setSelectedRole('');
            loadUsers();
          }
        } catch (error) {
          console.error('Error saving user:', error);
          message.error(error.message || 'Failed to save user. Please try again.');
        }
      })
      .catch(errorInfo => {
        if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
          message.error(errorInfo.errorFields[0].errors[0] || 'Please check the form fields');
        } else {
          message.error('Please fill in all required fields');
        }
      });
  };

  const handleSendPasswordReset = async (userId) => {
    try {
      await UserManagementService.sendPasswordReset(userId);
      message.success('Password reset email sent successfully');
    } catch (error) {
      console.error('Error sending password reset:', error);
      message.error('Failed to send password reset email. Please try again.');
    }
  };

  // ── Unlock user handler ──
  const handleUnlockUser = async (user) => {
    try {
      const response = await UserManagementService.unlockUser(user._id);
      if (response.success) {
        message.success(response.message || `Account unlocked for ${user.fullName || user.email}`);
        loadUsers();
      } else {
        message.error(response.message || 'Failed to unlock user');
      }
    } catch (error) {
      console.error('Error unlocking user:', error);
      message.error('Failed to unlock user account. Please try again.');
    }
  };

  // ── Credentials handlers ──
  const handleToggleHcwAccess = (user, enabled) => {
    if (enabled) {
      setHcwAccessUsers([user]);
      return;
    }
    Modal.confirm({
      title: `Turn off Deployed Hearts access for ${user.fullName || user.email}?`,
      content: 'They will be deactivated on the Healthcare Workers page and can no longer claim events. Their documents and history are kept, and you can turn access back on at any time.',
      okText: 'Turn off',
      okButtonProps: { danger: true },
      onOk: async () => {
        setHcwBusyUserId(user._id);
        try {
          const response = await UserManagementService.setHcwAccess(user._id, { enabled: false });
          message.success(response.message);
          loadUsers();
        } catch (error) {
          message.error(error.message || 'Failed to turn off Deployed Hearts access');
        } finally {
          setHcwBusyUserId(null);
        }
      }
    });
  };

  const handleResendHcwInvite = async (user) => {
    setHcwBusyUserId(user._id);
    try {
      const { invite } = await UserManagementService.resendHcwInvite(user._id);
      const failures = [
        invite.email && !invite.email.success && `email: ${invite.email.error}`,
        invite.sms && !invite.sms.success && `text: ${invite.sms.error}`
      ].filter(Boolean);
      if (failures.length) message.warning(`Invite partly failed — ${failures.join('; ')}`, 6);
      else message.success(`Invite sent to ${user.fullName || user.email}`);
      loadUsers();
    } catch (error) {
      message.error(error.message || 'Failed to send invite');
    } finally {
      setHcwBusyUserId(null);
    }
  };

  const handleHcwAccessDone = () => {
    setHcwAccessUsers([]);
    setSelectedUserKeys([]);
    setSelectedUserRows([]);
    loadUsers();
  };

  const handleCopyCredentials = () => {
    const credentialsText = `Login Credentials for ${credentialsModal.userName}\n\nEmail: ${credentialsModal.userEmail}\nTemporary Password: ${credentialsModal.tempPassword}\n\nPlease change your password after first login.`;
    navigator.clipboard.writeText(credentialsText).then(() => {
      message.success('Credentials copied to clipboard!');
    }).catch(() => {
      message.error('Failed to copy credentials');
    });
  };

  const handleCloseCredentialsModal = () => {
    setCredentialsModal({ visible: false, userEmail: '', tempPassword: '', userName: '' });
    message.success('User created successfully');
  };

  // ── Waiver handlers ──
  const handleViewWaiver = async (user) => {
    if (!user.email) {
      message.error('No email address for this volunteer');
      return;
    }

    setWaiverModal({
      visible: true, loading: true, registrations: [],
      volunteerName: user.fullName || 'Unknown',
      volunteerEmail: user.email
    });

    try {
      const response = await VolunteerRegistrationService.getRegistrationsByEmail(user.email);
      
      if (response.success && response.data) {
        setWaiverModal(prev => ({ ...prev, loading: false, registrations: response.data }));
      } else {
        setWaiverModal(prev => ({ ...prev, loading: false, registrations: [] }));
        message.info('No waiver/consent forms found for this volunteer');
      }
    } catch (error) {
      console.error('Error fetching waiver:', error);
      setWaiverModal(prev => ({ ...prev, loading: false, registrations: [] }));
      message.error('Failed to load waiver information');
    }
  };

  const handleCloseWaiverModal = () => {
    setWaiverModal({ visible: false, loading: false, registrations: [], volunteerName: '', volunteerEmail: '' });
  };

  // ── Pagination handler ──
  const handlePaginationChange = (page, pageSize) => {
    setPagination(prev => ({ ...prev, current: page, pageSize }));
    loadUsers({ page, limit: pageSize });
  };

  // ── Render ──
  return (
    <>
      <GlobalSelectStyles />
      <PageContainer style={{ minHeight: '100vh' }}>
        <UserManagementHeader 
          activeTab={activeTab}
          onTabChange={setActiveTab}
          userCounts={userCounts}
        />
        
        <FilterDrawer
          visible={filterDrawerVisible}
          onClose={() => setFilterDrawerVisible(false)}
          roleFilter={roleFilter}
          statusFilter={statusFilter}
          onRoleChange={setRoleFilter}
          onStatusChange={setStatusFilter}
        />
      
        {/* Search Bar - hide on registrations tab */}
        {activeTab !== 'registrations' && (
          <SearchFilterBar
            searchText={searchText}
            onSearchChange={setSearchText}
            onFilterClick={() => setFilterDrawerVisible(true)}
            onAddUser={handleAddUser}
            activeTab={activeTab}
          />
        )}
        
        {activeTab !== 'registrations' && selectedUserKeys.length > 0 && (
          <Alert
            type="info"
            style={{ marginBottom: 12 }}
            message={`${selectedUserKeys.length} user${selectedUserKeys.length === 1 ? '' : 's'} selected`}
            action={
              <Space>
                <Button size="small" type="primary" onClick={() => setHcwAccessUsers(selectedUserRows)}>
                  Give Deployed Hearts access
                </Button>
                <Button size="small" onClick={() => { setSelectedUserKeys([]); setSelectedUserRows([]); }}>
                  Clear
                </Button>
              </Space>
            }
          />
        )}

        {/* Tab Content */}
        {activeTab === 'registrations' ? (
          <RegistrationsTable
            registrations={registrations}
            registrationsLoading={registrationsLoading}
            registrationStatusFilter={registrationStatusFilter}
            onRegistrationStatusFilterChange={setRegistrationStatusFilter}
            registrationsPagination={registrationsPagination}
            onFetchRegistrations={fetchRegistrations}
            onUpdateRegistrationStatus={handleUpdateRegistrationStatus}
            onViewRegistrationWaiver={(record) => setRegistrationWaiverModal({ visible: true, registration: record })}
          />
        ) : (
          <UserDirectoryTable
            users={users}
            loading={loading}
            pagination={pagination}
            onPaginationChange={handlePaginationChange}
            onEditUser={handleEditUser}
            onViewWaiver={handleViewWaiver}
            onUnlockUser={handleUnlockUser}
            onToggleHcwAccess={handleToggleHcwAccess}
            onResendHcwInvite={handleResendHcwInvite}
            hcwBusyUserId={hcwBusyUserId}
            selectedRowKeys={selectedUserKeys}
            onSelectionChange={(keys, rows) => {
              setSelectedUserKeys(keys);
              // Keep rows selected on other pages (preserveSelectedRowKeys).
              setSelectedUserRows((prev) => {
                const byId = new Map([...prev, ...rows.filter(Boolean)].map((u) => [u._id, u]));
                return keys.map((key) => byId.get(key)).filter(Boolean);
              });
            }}
          />
        )}

        {/* Modals */}
        <UserFormModal
          visible={isModalVisible}
          editingUser={editingUser}
          form={form}
          selectedPermissions={selectedPermissions}
          setSelectedPermissions={setSelectedPermissions}
          selectedRole={selectedRole}
          setSelectedRole={setSelectedRole}
          onOk={handleModalOk}
          onCancel={() => {
            setIsModalVisible(false);
            setSelectedPermissions([]);
            setSelectedRole('');
            form.resetFields();
          }}
          onDeleteUser={handleDeleteUser}
          onSendPasswordReset={handleSendPasswordReset}
          isSelf={!!(editingUser && authStore.me && String(editingUser._id) === String(authStore.me.id))}
        />

        <CredentialsModal
          credentialsModal={credentialsModal}
          onCopyCredentials={handleCopyCredentials}
          onClose={handleCloseCredentialsModal}
        />

        <WaiverModal
          waiverModal={waiverModal}
          onClose={handleCloseWaiverModal}
        />

        <RegistrationWaiverModal
          registrationWaiverModal={registrationWaiverModal}
          onClose={() => setRegistrationWaiverModal({ visible: false, registration: null })}
        />

        <HcwAccessModal
          open={hcwAccessUsers.length > 0}
          users={hcwAccessUsers}
          onClose={() => setHcwAccessUsers([])}
          onDone={handleHcwAccessDone}
        />

        <SelectVolunteerModal
          selectVolunteerModal={selectVolunteerModal}
          onClose={() => setSelectVolunteerModal({ visible: false, loading: false, approvedRegistrations: [], creatingUserId: null })}
          onCreateUserFromRegistration={handleCreateUserFromRegistration}
        />
      </PageContainer>
    </>
  );
};

export default UserManagement;
